import { Fragment, useState } from "react";
import { cn } from "@/lib/utils";
import {
  EDITIONS,
  formatDayLong,
  formatPrice,
  formatRaceTime,
  topTipWins,
} from "@/utils/race-results";
import { ChevronDown } from "lucide-react";
import {
  Cloth,
  DayStatus,
  EditionPills,
  EmptyState,
  TipsDetail,
} from "./parts";

const ROWS_PER_DAY = 8;

const toggle = (set, value) => {
  const next = new Set(set);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
};

/** One row per race, grouped by day; tap a row for all 12 tips. */
export default function ResultsList({ days, latestIso }) {
  const [openRaces, setOpenRaces] = useState(() => new Set());
  const [fullDays, setFullDays] = useState(() => new Set());

  const visible = days.filter((d) => d.status !== "success" || d.races.length);
  if (!visible.length) {
    return (
      <EmptyState>
        No races match. Try another date or clear the search.
      </EmptyState>
    );
  }

  return (
    <div className="grid gap-4">
      {visible.map((day) => {
        const label = formatDayLong(day.iso);
        if (day.status !== "success")
          return <DayStatus key={day.iso} day={day} label={label} />;

        const total = day.races.length;
        const shown = fullDays.has(day.iso)
          ? day.races
          : day.races.slice(0, ROWS_PER_DAY);
        return (
          <section
            key={day.iso}
            className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-lg shadow-black/[0.04]"
          >
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-gray-100 px-4 py-3.5">
              <h3 className="text-base font-extrabold tracking-tight md:text-[17px]">
                {label}
                {day.iso === latestIso && (
                  <span className="ml-2 text-[13px] font-medium text-gray-500">
                    Yesterday
                  </span>
                )}
              </h3>
              <div className="flex flex-wrap gap-1.5 text-xs tabular-nums text-gray-600">
                <span className="rounded-md border border-gray-100 bg-gray-50 px-2 py-0.5">
                  {total} races
                </span>
                {EDITIONS.filter(({ key }) =>
                  day.races.some((r) => r[key]?.length),
                ).map(({ key, label: editionLabel }) => (
                  <span
                    key={key}
                    className="rounded-md border border-gray-100 bg-gray-50 px-2 py-0.5"
                  >
                    {editionLabel} Top Tip{" "}
                    <b className="text-[#C8007E]">
                      {topTipWins(day.races, key)}
                    </b>{" "}
                    won
                  </span>
                ))}
              </div>
            </div>

            {shown.map((race) => {
              const open = openRaces.has(race.id);
              const time = formatRaceTime(race.start);
              return (
                <Fragment key={race.id}>
                  <button
                    type="button"
                    onClick={() => setOpenRaces((s) => toggle(s, race.id))}
                    aria-expanded={open}
                    className={cn(
                      "grid w-full items-center gap-x-3 gap-y-1.5 border-t border-gray-100 px-4 py-3 text-left text-sm first-of-type:border-t-0 hover:bg-gray-50",
                      "grid-cols-[minmax(0,1fr)_auto_16px] [grid-template-areas:'rc_p_chev'_'w_w_w'_'pl_pl_pl']",
                      "md:grid-cols-[72px_minmax(120px,1.1fr)_minmax(150px,1.5fr)_64px_auto_16px] md:py-2.5 md:[grid-template-areas:'t_rc_w_p_pl_chev']",
                    )}
                  >
                    <span className="hidden text-[13px] tabular-nums text-gray-500 [grid-area:t] md:block">
                      {time}
                    </span>
                    <span className="min-w-0 [grid-area:rc]">
                      <b className="font-bold">
                        {race.track} R{race.race}
                      </b>
                      {race.state && (
                        <small className="ml-1.5 text-xs font-semibold text-gray-400">
                          {race.state}
                        </small>
                      )}
                      {time && (
                        <span className="ml-1.5 text-[12.5px] tabular-nums text-gray-500 md:hidden">
                          {time}
                        </span>
                      )}
                    </span>
                    <span className="flex min-w-0 items-center gap-2 font-bold [grid-area:w]">
                      <Cloth no={race.winner.no} />
                      <span className="truncate">
                        {race.winner.name || "Winner unavailable"}
                      </span>
                    </span>
                    <span className="text-right font-semibold tabular-nums [grid-area:p]">
                      {formatPrice(race.winner.sp)}
                    </span>
                    <EditionPills
                      race={race}
                      className="[grid-area:pl] md:justify-end"
                    />
                    <ChevronDown
                      className={cn(
                        "h-4 w-4 text-gray-400 transition-transform [grid-area:chev]",
                        open && "rotate-180",
                      )}
                      aria-hidden="true"
                    />
                  </button>
                  {open && <TipsDetail race={race} />}
                </Fragment>
              );
            })}

            {total > shown.length && (
              <div className="border-t border-gray-100 px-4 py-2.5">
                <button
                  type="button"
                  onClick={() => setFullDays((s) => toggle(s, day.iso))}
                  className="rounded-lg border border-gray-200 px-3.5 py-2 text-[13.5px] font-semibold hover:border-[#F0A7D5] hover:text-[#C8007E]"
                >
                  Show all {total} races
                </button>
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
